import React, { useState, useEffect } from 'react';
import { dataService } from '../../Services/data.service';
import { makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { bookingTypes } from '../../Config/constants'
import { ArrowLeft, ArrowRight, Check, RotateCcw, Scale, ScanBarcode, X } from 'lucide-react';
import FlagstoneTheme from '../../Config/flagstoneTheme';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';

export default function RegisterMaterial(props) {
    const classes = useStyles();
    const { t } = useTranslation();

    const [currentStep, setCurrentStep] = useState(1);
    const [category, setCategory] = useState(1);
    const [breadCrumbs, setBreadCrumbs] = useState([]);

    // Weighing
    const [weighingManually, setWeighingManually] = useState(false);
    const [totalWeight, setTotalWeight] = useState("");

    const [netWeight, setNetWeight] = useState(null);

    // Selection lists
    const [scrapColors, setScrapColors] = useState([]);
    const [scrapCarrierTypes, setScrapCarrierTypes] = useState([]);
    const [scrapMaterialTypes, setScrapMaterialTypes] = useState([]);
    const [wasteLocations, setWasteLocations] = useState([]);

    // Selected values
    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const [selectedMaterialType, setSelectedMaterialType] = useState(null);
    const [selectedMaterialColor, setSelectedMaterialColor] = useState(null);
    const [selectedWasteLocation, setSelectedWasteLocation] = useState(null);

    const [barcode, setBarcode] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    const [loading, setLoading] = useState(false);
    const [enterManually, setEnterManually] = useState(false);
    const [normalKeyboardLayoutName, setNormalKeyboardLayoutName] = useState("default");

    useEffect(() => {
        dataService.getScrapCarrierTypes().then((response) => {
            setScrapCarrierTypes(response);
        }).catch((error) => {
            console.log(error);
        });

        dataService.getScrapMaterialTypes().then((response) => {
            setScrapMaterialTypes(response);
        }).catch((error) => {
            console.log(error);
        })

        dataService.getScrapColors().then((response) => {
            setScrapColors(response);
        }).catch((error) => {
            console.log(error);
        });

        dataService.getWasteLocations().then((response) => {
            setWasteLocations(response);
        }).catch((error) => {
            console.log(error);
        });
    }, []);

    useEffect(() => {
        switch (currentStep) {
            case 1:
                setBreadCrumbs([]);
                break;
            case 2:
                setBreadCrumbs([selectedCarrier.name, selectedMaterialType.name]);
                break;
            case 3:
                setBreadCrumbs([selectedCarrier.name, selectedMaterialType.name, selectedMaterialColor.colorName]);
                break;
            case 4:
                setBreadCrumbs([selectedCarrier.name, selectedMaterialType.name, selectedMaterialColor.colorName, selectedWasteLocation.name]);
                break;
            default:
                break;
        }
    }, [currentStep]);

    useEffect(() => {
        if (selectedCarrier) {
            setNetWeight(totalWeight - selectedCarrier.tarraWeight);
        }
    }, [totalWeight]);

    const nextStep = (nextCategory) => {
        setCurrentStep(currentStep + 1);
        if (nextCategory) {
            setCategory(category + 1);
        }
    }

    const previousStep = (previousCategory) => {
        setCurrentStep(currentStep - 1);
        if (previousCategory) {
            setCategory(category - 1);
            setWeighingManually(false);
        }
    }

    const reset = () => {
        setCurrentStep(1);
        setCategory(1);
        setBreadCrumbs([]);

        setWeighingManually(false);
        setTotalWeight("");
        setNetWeight(null);

        setBarcode(null);

        setSelectedCarrier(null);
        setSelectedMaterialColor(null);
        setSelectedWasteLocation(null);

        setErrorMessage("");
    }

    const handleCarrierSelection = (carrier) => {
        setSelectedCarrier(carrier);

        // Set selected material type based on carrier
        setSelectedMaterialType(scrapMaterialTypes.find(material => material.id === carrier.materialTypeId));
    }

    const resetScannedBarcode = () => {
        setBarcode(null);
        setErrorMessage("");
    }

    const startWeighing = () => {
        // TODO: Process weight from scale

        if (weighingManually) {
            setNetWeight(totalWeight - selectedCarrier.tarraWeight);
            nextStep();
            setWeighingManually(false);
        } else {
            dataService.getWeightForRecycle(props.workstation.selectedRecyclingWorkstationId).then((response) => {
                if (selectedCarrier.tarraWeight > response) {
                    //if weight is lower then tarra nothing is on the scale or the scale connection is interrupted
                    setErrorMessage(t("Place a carrier on the scale or enter weight manually"));
                } else {
                    setTotalWeight(response);
                    setNetWeight(response - selectedCarrier.tarraWeight);
                    nextStep();
                }
            }).catch((error) => {
                console.log(error);
                // If weighing is unsuccessful, show error message
                setErrorMessage(t("Something went wrong during the weighing; please try again or enter the weight manually"));
            });
        }
    }

    const enterWeightManually = () => {
        setWeighingManually(true);
        setTotalWeight("");
        setErrorMessage("");
    }

    // Inputfield input
    const handleWeightInput = (event) => {
        const input = event.target.value;
        if (!input) {
            setTotalWeight(0);
        } else {
            setTotalWeight(parseFloat(input));
        }
    }

    const handleWeightInputKeyDown = (event) => {
        if (event.key === "Enter") {
            if (totalWeight !== "" && totalWeight !== 0) {
                startWeighing();
            }
        }
    }

    // On-screen keyboard input
    const handleWeightChange = (input) => {
        if (input.isNaN || input === "" || input === null) {
            setTotalWeight("");
        } else {
            setTotalWeight(parseFloat(input));
        }


    }

    const handleBarcodeChange = (input) => {
        setBarcode(input);
    }

    const handleBarcodeInputKeyDown = (event) => {
        if (event.key === "Enter") {
            if (barcode !== "" && barcode !== null) {
                processMaterial();
            }
        }
    }

    const handleKeyPress = (button) => {
        if (button === "{shift}") {
            setNormalKeyboardLayoutName(normalKeyboardLayoutName === "default" ? "shift" : "default");
        }
    };

    // Keyboard layout
    const numericLayout = {
        default: [
            '1 2 3',
            '4 5 6',
            '7 8 9',
            '. 0 {bksp}'
        ]
    };

    const processMaterial = () => {
        // TODO: check if carrier of type 'bak' is already registered
        if (selectedCarrier.name.includes("box") && barcode === null) {
            nextStep();
        } else {
            setLoading(true);
            postRecyclingHistoryEntry();
        }
    }

    const postRecyclingHistoryEntry = () => {
        // Post recycling history entry
        dataService.postRecyclingHistoryEntry({
            bookingTypeId: bookingTypes.REGISTER,
            carrierTypeId: selectedCarrier.carrierTypeId,
            carrierBarcode: barcode,
            materialTypeId: selectedMaterialType.id,
            wasteLocationId: selectedWasteLocation.id,
            productId: selectedMaterialColor.id,
            machineId: props.workstation.selectedRecyclingWorkstationId,
            netWeight: netWeight,
            insertedByUserId: parseInt(props.user.userInfo.sub)
        }).then((response) => {
            // Set barcode if it was generated
            if (response.barcode) {
                setBarcode(response.barcode);
            }

            if (selectedCarrier.name.includes("box")) {
                nextStep();
            } else {
                setCurrentStep(currentStep + 2);
                setCategory(category + 1);
            }
            setLoading(false);
        }).catch((error) => {
            console.log(error);
            setErrorMessage(t(error));
            setLoading(false);
        });
    }

    const sortWasteLocations = (wasteLocations) => {
        return wasteLocations.sort((a, b) => {
            // Check if either id is 0, and prioritize it
            if (a.id === 0) return -1;
            if (b.id === 0) return 1;
            // Otherwise, sort alphabetically by name
            return a.name.localeCompare(b.name);
        });
    };

    // Stepper - Step component
    const Step = ({ stepNumber, stepTitle }) => {
        return (
            <div className={classes.step}>
                <div className={classes.step__number} style={{ backgroundColor: category > stepNumber ? "#5CC958" : category < stepNumber ? "#DCDCDC" : "#4654FB" }}>
                    {category > stepNumber ? <Check size={20} /> : stepNumber}
                </div>
                <p className={classes.step__title} style={{ color: category > stepNumber ? "#5CC958" : category < stepNumber ? "#DCDCDC" : "#4654FB" }}>
                    {t(stepTitle)}
                </p>
                <div className={classes.step__progressBarContainer}>
                    <div className={classes.step__progressBarBackground} />
                    <div className={`${classes.step__progressBar} ${category > stepNumber ? classes.step__progressBar__full : null}`} />
                </div>
            </div>
        )
    }

    return (
        <div className={classes.registerContainer} >
            <div className={classes.stepper}>
                <Step stepNumber={1} stepTitle="Details ingeven" />
                <Step stepNumber={2} stepTitle="Wegen" />
                <Step stepNumber={3} stepTitle="Stockeren" />
            </div>
            <div className={classes.currentStepContainer}>
                <div className={classes.breadCrumbContainer}>
                    {breadCrumbs.length > 0 && currentStep < 5 && breadCrumbs.map((crumb, index) => {
                        return (
                            <p key={index} className={classes.breadCrumb}>
                                {t(crumb)}
                            </p>
                        )
                    })}
                </div>
                <div className={classes.currentStepCard}>
                    {/* Carrier type */}
                    {currentStep === 1 && <>
                        <h1 className={classes.currentStep__title}>{t("Carrier type")}</h1>
                        <div className={classes.interactionContainer}>
                            <div className={classes.carrierContainer}>
                                {scrapCarrierTypes && scrapCarrierTypes.length > 0 && scrapCarrierTypes.map((carrier, index) => {
                                    return (
                                        <div key={index} className={classes.carrier} onClick={() => handleCarrierSelection(carrier)}>
                                            <img className={classes.carrier__image} src={process.env.PUBLIC_URL + '/img/' + carrier.directory} alt={carrier.type} />
                                            <div className={classes.carrier__radioButton}>
                                                <div className={classes.radio} style={{ backgroundColor: `${selectedCarrier && selectedCarrier.name == carrier.name ? "#4654FB" : "#EDEDED"}`, border: `${selectedCarrier && selectedCarrier.name == carrier.name ? "none" : "1px solid #0C0E29"}` }}>
                                                    <Check className={classes.radioIcon} style={{ display: `${selectedCarrier && selectedCarrier.name == carrier.name ? "block" : "none"}` }} size={16} />
                                                </div>
                                                <p className={classes.carrier__radioLabel}>{t(carrier.name)}</p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                        <button
                            className={`${classes.currentStep__nextStepButton} ${selectedCarrier === null ? 'disabled' : ''}`}
                            onClick={() => {
                                if (selectedCarrier !== null) {
                                    nextStep();
                                }
                            }}
                        >
                            {t("Material color")}
                            <ArrowRight size={24} />
                        </button>
                    </>}
                    {/* Material color */}
                    {currentStep === 2 && <>
                        <h1 className={classes.currentStep__title}>{t("Material color")}</h1>
                        <div className={classes.interactionContainer}>
                            <div className={classes.colorContainer}>
                                {scrapColors && scrapColors.length > 0 && scrapColors.map((color, index) => {
                                    return (
                                        <div key={index} className={classes.color} onClick={() => setSelectedMaterialColor(color)}>
                                            <div className={classes.radio} style={{ backgroundColor: `${selectedMaterialColor == color ? "4654FB" : "#EDEDED"}`, border: `${selectedMaterialColor == color ? "none" : "1px solid #0C0E29"}` }}>
                                                <Check className={classes.radioIcon} style={{ display: `${selectedMaterialColor == color ? "block" : "none"}` }} size={16} />
                                            </div>
                                            <p className={classes.color__label}>{color.colorName}</p>
                                            <div className={classes.color__indicator} style={{ backgroundColor: color.colorHex }} />
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                        <button className={classes.currentStep__previousStepButton} onClick={() => previousStep()}>
                            <ArrowLeft size={24} />
                            {t("Material type")}
                        </button>
                        <button
                            className={`${classes.currentStep__nextStepButton} ${selectedMaterialColor === null ? 'disabled' : ''}`}
                            onClick={() => {
                                if (selectedMaterialColor !== null) {
                                    nextStep();
                                }
                            }}
                        >
                            {t("Waste location")}
                            <ArrowRight size={24} />
                        </button>
                    </>}
                    {/* Waste location */}
                    {currentStep === 3 && <>
                        <h1 className={classes.currentStep__title}>{t("Waste location")}</h1>
                        <p className={classes.currentStep__subtitle}>{t("Where did the material come from?")}</p>
                        <div className={classes.interactionContainer}>
                            <div className={classes.wasteLocationContainer}>
                                {wasteLocations && wasteLocations.length > 0 && sortWasteLocations(wasteLocations).map((wasteLocation, index) => {
                                    return (
                                        <div key={index} className={classes.wasteLocation} onClick={() => setSelectedWasteLocation(wasteLocation)}>
                                            <div className={classes.radio} style={{ backgroundColor: `${selectedWasteLocation == wasteLocation ? "4654FB" : "#EDEDED"}`, border: `${selectedWasteLocation == wasteLocation ? "none" : "1px solid #0C0E29"}` }}>
                                                <Check className={classes.radioIcon} style={{ display: `${selectedWasteLocation == wasteLocation ? "block" : "none"}` }} size={16} />
                                            </div>
                                            <p className={classes.color__label}>{t(wasteLocation.name)}</p>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                        <button className={classes.currentStep__previousStepButton} onClick={() => previousStep()}>
                            <ArrowLeft size={24} />
                            {t("Material color")}
                        </button>
                        <button
                            className={`${classes.currentStep__nextStepButton} ${selectedCarrier === null ? 'disabled' : ''}`}
                            onClick={() => {
                                if (selectedCarrier !== null) {
                                    nextStep(true);
                                }
                            }}
                        >
                            {t("Weigh")}
                            <ArrowRight size={24} />
                        </button>
                    </>}
                    {/* Weighing */}
                    {currentStep === 4 && <>
                        <h1 className={classes.currentStep__title}>{t("Weighing")}</h1>
                        {!errorMessage ?
                            weighingManually ?
                                <>
                                    <div className={classes.interactionContainer}>
                                        <div className={classes.verticalContainer}>
                                            <Scale size={80} absoluteStrokeWidth strokeWidth={3} />
                                            <div className={classes.inputContainer}>
                                                <input className={classes.inputField} autoFocus type="number" placeholder="Gewicht" onChange={handleWeightInput} onKeyDown={(e) => handleWeightInputKeyDown(e)} value={totalWeight} />
                                                <p className={classes.inputAddendum}>KG</p>
                                            </div>
                                            <Keyboard theme={`${"hg-theme-default"} ${classes.numericKeyboard}`} layout={numericLayout} onChange={handleWeightChange} />
                                        </div>
                                    </div>
                                    <button
                                        className={`${classes.currentStep__nextStepButton} ${totalWeight === "" || totalWeight === 0 || totalWeight < selectedCarrier.tarraWeight ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (selectedMaterialColor !== null) {
                                                startWeighing();
                                            }
                                        }}
                                    >
                                        {t("Submit")}
                                        <ArrowRight size={24} />
                                    </button>
                                </>
                                :
                                <>

                                    <p className={classes.currentStep__subtitle}>{t("Make sure that the carrier is on the scale")}</p>
                                    <div className={classes.interactionContainer}>
                                        <div className={classes.verticalContainer}>
                                            <Scale size={80} absoluteStrokeWidth strokeWidth={3} />
                                            <button className={classes.currentStep__primaryButton} onClick={() => startWeighing()}>
                                                {t("Start weighing")}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            :
                            <div className={classes.interactionContainer}>
                                <div className={classes.verticalContainer}>
                                    <div className={classes.errorMessage}>{t(errorMessage)}</div>
                                    <div className={classes.currentStep__buttonContainer}>
                                        <button className={classes.currentStep__secondaryButton} onClick={() => enterWeightManually()}>
                                            {t("Enter manually")}
                                        </button>
                                        <button className={classes.currentStep__primaryButton} onClick={() => startWeighing()}>
                                            {t("Weigh again")}
                                        </button>
                                    </div>

                                </div>
                            </div>
                        }
                        <button className={classes.currentStep__previousStepButton} onClick={() => previousStep(true)}>
                            <ArrowLeft size={24} />
                            {t("Waste location")}
                        </button >
                    </>}
                    {/* Weighing results */}
                    {currentStep === 5 && <>
                        <h1 className={classes.currentStep__title}>{t("Weight")}</h1>
                        {!errorMessage ?
                            <>
                                <div className={classes.interactionContainer}>
                                    {loading ?
                                        <div className={classes.loaderContainer}>
                                            <span className={classes.loader} />
                                        </div>
                                        :
                                        <div className={classes.verticalContainer}>
                                            <Scale size={80} absoluteStrokeWidth strokeWidth={3} />
                                            <div className={classes.weightSummary}>
                                                <p className={classes.weight}>{t("Total weight")}: <span style={{ fontWeight: "bold" }}>{totalWeight.toFixed(2)}kg</span></p>
                                                <p className={classes.weight}>{t("Net weight")}: <span style={{ fontWeight: "bold" }}>{netWeight.toFixed(2)}kg</span></p>
                                            </div>
                                            <div className={classes.breadCrumbContainer}>
                                                {breadCrumbs.length > 0 && breadCrumbs.map((crumb, index) => {
                                                    return (
                                                        <p key={index} className={classes.breadCrumb}>
                                                            {t(crumb)}
                                                        </p>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    }
                                </div>
                                <button className={`${classes.currentStep__previousStepButton} ${loading ? 'disabled' : ''}`}
                                    onClick={() => !loading && previousStep()}>
                                    <RotateCcw size={24} />
                                    {t("Weigh again")}
                                </button>
                                <button className={`${classes.currentStep__nextStepButton} ${loading ? 'disabled' : ''}`} onClick={() => !loading && processMaterial()}>
                                    {selectedCarrier.name.includes("box") ? t("Scan barcode") : t("Process")}
                                    <ArrowRight size={24} />
                                </button>
                            </>
                            :
                            <>
                                <div className={classes.interactionContainer}>
                                    {loading ?
                                        <div className={classes.loaderContainer}>
                                            <span className={classes.loader} />
                                        </div>
                                        :
                                        <div className={classes.verticalContainer}>
                                            <div className={classes.errorMessage}>{t(errorMessage)}</div>
                                        </div>
                                    }
                                </div >
                                <button className={`${classes.currentStep__previousStepButton} ${loading ? 'disabled' : ''}`} onClick={() => reset()}>
                                    <X size={24} />
                                    {t("Cancel")}
                                </button>
                                <button className={`${classes.currentStep__nextStepButton} ${loading ? 'disabled' : ''}`} onClick={() => processMaterial()}>
                                    {t("Try again")}
                                    <RotateCcw size={24} />
                                </button>
                            </>
                        }
                    </>}
                    {/* Scan barcode */}
                    {currentStep === 6 && <>
                        <h1 className={classes.currentStep__title}>{t("Scan barcode")}</h1>
                        {!errorMessage ?
                            <>
                                {loading ?
                                    <div className={classes.interactionContainer}>
                                        <div className={classes.loaderContainer}>
                                            <span className={classes.loader} />
                                        </div>
                                    </div>
                                    :
                                    <>
                                        <p className={classes.currentStep__subtitle}>{t("Enter the barcode found on the carrier")}</p>
                                        <div className={classes.interactionContainer}>
                                            <div className={classes.verticalContainer}>
                                                <div className={classes.inputContainer} style={{ width: "320px" }}>
                                                    <input className={classes.inputField} autoFocus type="text" placeholder="Barcode" onChange={(e) => setBarcode(e.target.value)} onKeyDown={(e) => handleBarcodeInputKeyDown(e)} value={barcode} />
                                                    <ScanBarcode size={24} className={classes.inputAddendum} />
                                                </div>
                                                {!enterManually ?
                                                    <button className={classes.currentStep__secondaryButton} onClick={() => setEnterManually(true)}>{t("Enter manually")}</button>
                                                    :
                                                    <Keyboard layoutName={normalKeyboardLayoutName} onChange={handleBarcodeChange} onKeyPress={handleKeyPress} />
                                                }
                                            </div>
                                        </div>
                                        <button className={`${classes.currentStep__nextStepButton} ${!barcode || barcode.trim() == "" ? 'disabled' : ''}`} onClick={() => processMaterial()}>
                                            {t("Process")}
                                            <ArrowRight size={24} />
                                        </button>
                                    </>
                                }
                            </>
                            :
                            <>
                                <div className={classes.interactionContainer}>
                                    <div className={classes.verticalContainer}>
                                        <div className={classes.barcodeDisplay}>
                                            <ScanBarcode size={28} color='#0C0E29' />
                                            <p className={classes.barcodeDisplay__barcode}>{barcode}</p>
                                        </div>
                                        <div className={classes.errorMessage}>{t(errorMessage)}</div>
                                        <div className={classes.currentStep__buttonContainer}>
                                            <button className={classes.currentStep__secondaryButton} onClick={() => reset()}>
                                                {t("Cancel")}
                                            </button>
                                            <button className={classes.currentStep__primaryButton} onClick={() => resetScannedBarcode()}>
                                                {t("Scan again")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                    </>}
                    {/* Finished */}
                    {currentStep === 7 && <>
                        <h1 className={classes.currentStep__title}>{t("Stock")}</h1>
                        <div className={classes.confirmation}>
                            {t("The waste material was successfully processed")}
                        </div>
                        <div className={classes.barcodeDisplay}>
                            <ScanBarcode size={28} color='#0C0E29' />
                            <p className={classes.barcodeDisplay__barcode}>{barcode}</p>
                        </div>
                        <div className={classes.interactionContainerCentered}>
                            <div className={classes.StockStepsContainer}>
                                <div className={classes.step}>
                                    <div className={classes.step__number} style={{ backgroundColor: "#4654FB" }}>
                                        1
                                    </div>
                                    <p className={classes.step__title} style={{ color: "#0C0E29" }}>
                                        {t("Place the printed label on the carrier")}
                                    </p>
                                </div>
                                <div className={classes.step}>
                                    <div className={classes.step__number} style={{ backgroundColor: "#4654FB" }}>
                                        2
                                    </div>
                                    <p className={classes.step__title} style={{ color: "#0C0E29" }}>
                                        {t("Place the waste material in an appropriate location in the warehouse")}
                                    </p>
                                </div>
                                <div className={classes.step}>
                                    <div className={classes.step__number} style={{ backgroundColor: "#4654FB" }}>
                                        3
                                    </div>
                                    <p className={classes.step__title} style={{ color: "#0C0E29" }}>
                                        {t("Scan the barcode when the waste material is in place")}
                                    </p>
                                </div>
                            </div>
                            <button className={classes.currentStep__primaryButton} style={{ marginTop: "32px" }} onClick={() => reset()}>
                                {t("Book more waste material")}
                            </button>
                        </div>
                    </>}

                </div>
            </div>
        </div >
    )
}

const useStyles = makeStyles(() => ({
    registerContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "31px",

        width: "100%",
        height: "100%",
    },
    stepper: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: "24px",
    },
    step: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "12px",

        // last step progressbar not visibible
        "&:last-child $step__progressBarContainer": {
            display: "none",
        },
    },

    step__number: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        backgroundColor: "#4654FB",
        color: "#F8F8F8",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "18px",
        fontFamily: "Lato, sans-serif",

        transition: "background-color 0.3s",
    },

    step__title: {
        fontSize: "18px",
        fontFamily: "Lato, sans-serif",
        color: "#4654FB",

        margin: "0",

        transition: "color 0.3s",
    },

    step__progressBarContainer: {
        width: "124px",
        marginLeft: "12px",

        display: "flex",
        flexDirection: "column",
        alignItems: "center",

        position: "relative",

    },

    step__progressBarBackground: {
        width: "100%",
        height: "2px",
        backgroundColor: "#DCDCDC",
    },

    step__progressBar: {
        width: "0%",
        height: "2px",
        backgroundColor: "#5CC958",

        transition: "all 0.3s",

        position: "absolute",
        top: "0",
        left: "0",
    },

    step__progressBar__full: {
        width: "100%",
    },

    currentStepContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",

        gap: "8px",
    },

    breadCrumbContainer: {
        height: "25px",

        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "8px",
    },

    breadCrumb: {
        fontSize: "14px",
        fontFamily: "Lato, sans-serif",
        backgroundColor: "#DEE8FF",
        color: "#4654FB",
        borderRadius: "13px",

        margin: "0",
        padding: "4px 12px",
    },

    currentStepCard: {
        minWidth: "800px",
        minHeight: "520px",

        borderRadius: "6px",
        backgroundColor: "white",

        // dropshadow
        boxShadow: "0px 3px 16px rgba(175, 175, 175, 0.25)",

        display: "flex",
        flexDirection: "column",
        alignItems: "center",

        position: "relative",

        padding: "16px",
        paddingTop: "32px",
    },

    currentStep__title: {
        fontSize: "24px",
        fontFamily: "Lato, sans-serif",
        fontWeight: "normal",
        color: "#0C0E29",

        margin: "0",
    },

    currentStep__subtitle: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        color: "#0C0E29",
        opacity: "0.8",

        margin: "0",
        marginTop: "16px",
    },

    currentStep__buttonContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",
    },

    currentStep__primaryButton: {
        height: "40px",

        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        padding: "8px 16px",

        borderRadius: "3px",
        backgroundColor: "#4654FB",
        color: "#F8F8F8",

        fontSize: "16px",
        fontFamily: "Lato, sans-serif",

        border: "none",

        transition: "background-color 0.3s",

        "&:hover": {
            backgroundColor: "#3B3AF1",

            cursor: "pointer",

            transition: "background-color 0.3s",
        },

        "&.disabled": {
            backgroundColor: "#DCDCDC",
            color: "#989898",
            cursor: "not-allowed",
            border: "none",
        }
    },

    currentStep__secondaryButton: {
        height: "40px",

        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        padding: "8px 16px",

        borderRadius: "3px",
        backgroundColor: "transparent",
        color: "#4654FB",

        fontSize: "16px",
        fontFamily: "Lato, sans-serif",

        boxSizing: "border-box",
        border: "1px solid #4654FB",

        transition: "background-color 0.3s",

        "&:hover": {
            backgroundColor: "#3B3AF1",
            color: "#F8F8F8",

            cursor: "pointer",

            transition: "background-color 0.3s",
        },

        "&.disabled": {
            backgroundColor: "#DCDCDC",
            color: "#989898",
            cursor: "not-allowed",
            border: "none",
        }
    },

    currentStep__nextStepButton: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        padding: "8px",
        paddingLeft: "16px",

        borderRadius: "3px",
        backgroundColor: "#4654FB",
        color: "#F8F8F8",

        fontSize: "16px",
        fontFamily: "Lato, sans-serif",

        border: "none",

        position: "absolute",
        bottom: "16px",
        right: "16px",

        transition: "background-color 0.3s",

        "&:hover": {
            backgroundColor: "#3B3AF1",

            cursor: "pointer",

            transition: "background-color 0.3s",
        },

        "&.disabled": {
            backgroundColor: "#DCDCDC",
            color: "#989898",
            cursor: "not-allowed",
            border: "none",
        }
    },

    currentStep__previousStepButton: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        padding: "8px",
        paddingRight: "16px",

        boxSizing: "border-box",

        borderRadius: "3px",
        border: "1px solid #4654FB",
        backgroundColor: "transparent",
        color: "#4654FB",

        fontSize: "16px",
        fontFamily: "Lato, sans-serif",

        position: "absolute",
        bottom: "16px",
        left: "16px",

        transition: "background-color 0.3s",

        "&:hover": {
            backgroundColor: "#3B3AF1",
            color: "#F8F8F8",

            cursor: "pointer",

            transition: "background-color 0.3s",
        },

        "&.disabled": {
            backgroundColor: "#DCDCDC",
            color: "#989898",
            cursor: "not-allowed",
            border: "none",
        }
    },

    interactionContainer: {
        width: "calc(100% - 32px)",

        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
    },

    interactionContainerCentered: {
        width: "calc(100% - 32px)",

        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",

        position: "absolute",
        top: "50%",
        transform: "translateY(-50%) translateY(40px)",
    },

    verticalContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "24px",
    },

    carrierContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "16px",
    },

    carrier: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        cursor: "pointer",

        "&:hover": {
            "& $carrier__radioButton": {
                backgroundColor: "#DCDCDC",

                transition: "background-color 0.2s",
            },
        },
    },

    carrier__image: {
        height: "148px",
        width: "100%",

        objectFit: "cover",

        borderRadius: "3px",
    },

    carrier__radioButton: {
        padding: "8px",
        paddingRight: "16px",

        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        borderRadius: "20px",

        backgroundColor: "#EDEDED",
    },

    carrier__radioLabel: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        color: "#0C0E29",

        margin: "0",
    },

    colorContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "16px",
    },

    color: {
        padding: "4px",
        paddingLeft: "12px",

        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        borderRadius: "24px",

        backgroundColor: "#EDEDED",

        cursor: "pointer",

        "&:hover": {
            backgroundColor: "#DCDCDC",

            transition: "background-color 0.2s",
        },
    },

    color__label: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        color: "#0C0E29",

        margin: "0",
    },

    color__indicator: {
        width: "40px",
        height: "40px",
        borderRadius: "50%",

        marginLeft: "8px",
    },



    radio: {
        width: "24px",
        height: "24px",

        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        boxSizing: "border-box",

        borderRadius: "50%",
        border: "1px solid #0C0E29",
        backgroundColor: "#EDEDED",
    },

    radioIcon: {
        color: "#F8F8F8",
    },

    wasteLocationContainer: {
        width: "480px",

        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "16px",

        margin: "auto",
    },

    wasteLocation: {
        padding: "8px",
        paddingRight: "16px",

        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        borderRadius: "20px",

        backgroundColor: "#EDEDED",

        cursor: "pointer",

        "&:hover": {
            backgroundColor: "#DCDCDC",
            transition: "background-color 0.2s",
        },
    },

    dropdown: {
        width: "256px",
        height: "40px",

        fontSize: "16px",
        fontFamily: "Lato, sans-serif",

        border: "1px solid #A9A9A9",
        borderRadius: "3px",
        boxSizing: "border-box",
        outline: "none",

        padding: "0px 8px",

        transition: "border-color 0.3s",

        "&:focus-within": {
            borderColor: "#4654FB",
        },
    },

    weightSummary: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",
    },

    weight: {
        fontSize: "24px",
        fontFamily: "Lato, sans-serif",

        margin: "0"
    },

    confirmation: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",

        color: "#5CC958",
        backgroundColor: "#F3FBF2",

        border: "1px solid #5CC958",
        borderRadius: "6px",

        padding: "12px",
        marginTop: "24px",
        marginBottom: "16px",
    },

    StockStepsContainer: {
        width: "fit-content",

        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: "16px",
    },

    errorMessage: {
        maxWidth: "400px",
        padding: "12px",
        borderRadius: "6px",

        backgroundColor: "#FDF3F3",
        color: "#D84949",

        border: "1px solid #D84949",

        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
    },

    inputContainer: {
        width: "256px",

        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",

        border: "1px solid #A9A9A9",
        borderRadius: "3px",
        boxSizing: "border-box",

        gap: "8px",

        transition: "border-color 0.3s",

        "&:focus-within": {
            borderColor: "#4654FB",
        },
    },

    inputField: {
        width: "100%",
        height: "40px",

        fontSize: "16px",
        fontFamily: "Lato, sans-serif",

        border: "none",
        outline: "none",

        marginLeft: "8px",

        // remove arrows
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
            "-webkit-appearance": "none",
            margin: 0,
        },

        // remove arrows (Firefox)
        "&[type=number]": {
            "-moz-appearance": "textfield",
        },
    },

    inputAddendum: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        fontWeight: "bold",
        color: "#0C0E29",

        margin: "0",
        marginRight: "8px",
    },

    barcodeDisplay: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",
    },

    barcodeDisplay__barcode: {
        fontSize: "20px",
        fontFamily: "Lato, sans-serif",
        fontWeight: "bold",
        color: "#0C0E29",

        margin: "0",
    },

    loaderContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },

    "@global": {
        "@keyframes rotation": {
            "0%": { transform: "rotate(0deg)" },
            "100%": { transform: "rotate(360deg)" },
        },
    },

    loader: {
        width: "48px",
        height: "48px",
        border: "5px solid " + FlagstoneTheme.palette.primary.main,
        borderBottomColor: "transparent",
        borderRadius: "50%",
        display: "inline-block",
        boxSizing: "border-box",
        animation: "rotation 1s linear infinite",
    },

    numericKeyboard: {
        width: "400px",
        height: "100%",
    },
}));